import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const Step = ({ n, children }) => (
  <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-600 text-xs font-bold text-white">{n}</span><span className="text-sm">{children}</span></li>
);

export default function InstallButton({ className = "" }) {
  const [prompt, setPrompt] = useState(window.__installPrompt || null);
  const [installed, setInstalled] = useState(standalone());
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const ready = () => setPrompt(window.__installPrompt);
    const done = () => setInstalled(true);
    window.addEventListener("installprompt-ready", ready);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("installprompt-ready", ready); window.removeEventListener("appinstalled", done); };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (!prompt) return setHelp(true);
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__installPrompt = null; setPrompt(null);
    if (outcome === "accepted") setInstalled(true);
  };

  return (
    <>
      <Button onClick={install} className={`bg-teal-600 hover:bg-teal-700 ${className}`} data-testid="install-app-button">
        <Download className="mr-2 h-4 w-4" /> Install app on this phone
      </Button>
      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle>Add InvoiceReconciler to your home screen</DialogTitle>
            <DialogDescription>It then opens full screen like a normal app, with the camera ready.</DialogDescription>
          </DialogHeader>
          {isIOS() ? (
            <ol className="space-y-3" data-testid="install-help-ios">
              <Step n={1}>Open this page in <b>Safari</b>.</Step>
              <Step n={2}>Tap the <b>Share</b> button <Share className="inline h-4 w-4" /> at the bottom of the screen.</Step>
              <Step n={3}>Scroll down and tap <b>Add to Home Screen</b> <PlusSquare className="inline h-4 w-4" />, then <b>Add</b>.</Step>
            </ol>
          ) : (
            <ol className="space-y-3" data-testid="install-help-android">
              <Step n={1}>Open this page in <b>Chrome</b>.</Step>
              <Step n={2}>Tap the menu <MoreVertical className="inline h-4 w-4" /> at the top right.</Step>
              <Step n={3}>Tap <b>Install app</b> (or <b>Add to Home screen</b>), then <b>Install</b>.</Step>
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
